import React, { useEffect, useState } from "react";

import CardProviderDetails from "../CardProviderDetails/CardProviderDetails";
import CentralLoadingIndicator from "../CentralLoadingIndicator/CentralLoadingIndicator";
import DefaultCardIndicator from "../DefaultCardIndicator/DefaultCardIndicator";
import { Divider } from "@mui/material";
import { ICardItemResponse } from "../../services/card-list/card-list.interface";
import { InfoIcon } from "../Icons";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { MODALS, EXPIRY_STATUS, ALERT_PAYMENT_PLAN_STATUS, CARD_EXPIRY_STATUS } from "../../config/params";
import RemoveIcon from "../Icons/RemoveIcon";
import UserProfileNextIcon from "../Icons/UserProfileNextIcon";
import { useCardAsDefault } from "../../services/card-as-default/card-as-default.service";
import { useCardDelete } from "../../services/card-delete/card-delete.service";
import { useCardDetailStore } from "../../services/card-detail/card-detail.service";
import { useModal } from "../../context/ModalContext";
import { userActivityLogger } from "../../utils/mixpanel";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { PAGES_PATH } from "../../navigation/params";

export interface IPaymentMethodCard {
  cardDetail: ICardItemResponse;
  isSelected?: boolean;
  onCardClick?: (card: ICardItemResponse) => void;
  disableExpand?: boolean;
}

type IconColorType = "disabled" | "primary" | "error" | "warning" | "info" |
"success" | "action" | "inherit" | "secondary" | undefined

const PaymentMethodCard = ({ cardDetail, isSelected, onCardClick, disableExpand }: IPaymentMethodCard) => {
  const { cardBrand, isDefault, last4, expMonth, expYear, id, hasSubscription } = cardDetail;
  const [expand, setExpand] = useState(false);
  const [removeWarning, setRemoveWarning] = useState(false);
  const [subscriptionWarning, setSubscriptionWarning] = useState(false);
  const { cardDelete, cardDeleted, loading, error } = useCardDelete();
  const formattedExpMonth = String(expMonth).padStart(2, "0");
  const { setCardUpdateStatus } = useCardDetailStore();
  const { setCardDefault, cardUpdated, loading: cardUpdateLoading } = useCardAsDefault();
  const { openModal } = useModal();


  const handleExpand = () => {
    if (!expand) {
      userActivityLogger(MIXPANEL_EVENTS.VISIT_CARD_DETAILS, {});
    }
    !disableExpand ? setExpand(!expand) : openModal(MODALS.UPDATE_PAYMENT_METHOD_MODAL);
  };

  const handleRemove = () => {
    userActivityLogger(MIXPANEL_EVENTS.REMOVE_PAYMENT_METHOD_CLICK, {});
    if (isDefault) {
      setRemoveWarning(true);
    } else if (hasSubscription) {
      setSubscriptionWarning(true);
    } else {
      cardDelete(id);
    }
  };

  const handleSetAsDefault = () => {
    userActivityLogger(MIXPANEL_EVENTS.SET_CARD_DEFAULT, {});
    setCardDefault(cardDetail.id);
  };

  useEffect(() => {
    if (cardDeleted) {
      userActivityLogger(MIXPANEL_EVENTS.REMOVE_CARD_DETAIL_SUCCESS, {});
      setCardUpdateStatus(true);
    }
    if (error) {
      userActivityLogger(MIXPANEL_EVENTS.REMOVE_CARD_DETAIL_FAILURE,
        { failure_reason: error.graphQLErrors[0]?.message });
    }
  }, [cardDeleted, error]);

  useEffect(() => {
    if (cardUpdated) {
      setCardUpdateStatus(true);
    }
  }, [cardUpdated]);

  const getCardStatusIndicator = (cardDetail: ICardItemResponse, isAlertType?:boolean) => {
    const { isActive, expiryStatus } = cardDetail;
    const isDisabled = isActive === false || expiryStatus?.toLowerCase() === EXPIRY_STATUS.DISABLED;
    const isExpiringSoon = expiryStatus?.toLowerCase() === EXPIRY_STATUS.EXPIRING_SOON;
    const isExpired = expiryStatus?.toLowerCase() === EXPIRY_STATUS.EXPIRED;
    const isIssuerAlert = expiryStatus?.toLowerCase() === EXPIRY_STATUS.ISSUER_REPORT;
    const statusConfig = {
      disabled: { bgColor: "bg-red-1", textColor: "text-red-5",
        color: "error", text: CARD_EXPIRY_STATUS.DISABLED,
        alertHeader: ALERT_PAYMENT_PLAN_STATUS.diabled.header, alertDesc: ALERT_PAYMENT_PLAN_STATUS.diabled.body },
      expiringSoon: { bgColor: "bg-gv-yellow-light", textColor: "text-gv-yellow-dark", color: "primary",
        alertHeader: ALERT_PAYMENT_PLAN_STATUS.expiringSoon.header, text: CARD_EXPIRY_STATUS.EXPIRING_SOON,
        alertDesc: ALERT_PAYMENT_PLAN_STATUS.expiringSoon.body },
      expired: { bgColor: "bg-red-1", textColor: "text-red-5", color: "error",
        alertHeader: ALERT_PAYMENT_PLAN_STATUS.expired.header, text: CARD_EXPIRY_STATUS.EXPIRED,
        alertDesc: ALERT_PAYMENT_PLAN_STATUS.expired.body,
      },
      issuerAlert: { bgColor: "bg-red-1", textColor: "text-red-5",
        color: "error", alertHeader: ALERT_PAYMENT_PLAN_STATUS.issuerReport.header,
        text: CARD_EXPIRY_STATUS.ISSUER_REPORT,
        alertDesc: ALERT_PAYMENT_PLAN_STATUS.issuerReport.body },
    };

    let status;

    if (isDisabled) status = statusConfig.disabled;
    else if (isExpiringSoon) status = statusConfig.expiringSoon;
    else if (isExpired) status = statusConfig.expired;
    else if (isIssuerAlert) status = statusConfig.issuerAlert;
    const isPaymentPage = window.location.pathname.includes(`/${PAGES_PATH.USER_BILLING}/${PAGES_PATH.BILLING_METHOD}`);

    if (isAlertType && !isPaymentPage && disableExpand) {
      return status ? (
        <div className={`${status.bgColor} ${status.textColor}  rounded-lg p-3 text-sm`}>
          <p className="font-semibold flex gap-1">
            <WarningRoundedIcon color={status.color as IconColorType}/>
            <span className="relative top-1">{status.alertHeader}</span></p>
          <p>{status.alertDesc}</p>
        </div>): null;
    } else {
      return status && !disableExpand && !isAlertType ? (
        <div className={`flex items-center ${status.bgColor} ${status.textColor}
           text-[0.6rem] py-0.5 px-5 rounded-2xl capitalize`}>
          {status.text}
        </div>
      ) : null;
    }
  };

  return (
    <>
      <div
        onClick={() => onCardClick && onCardClick(cardDetail)}
        className={`flex flex-col justify-center items-center
           ${isSelected ? "bg-gv-yellow-light" : "bg-white" } px-4 pt-2
        gap-4 rounded-lg border border-taupe-2 cursor-default`}
      >
        <div className="w-full flex flex-col items-center">
          <div onClick={handleExpand} className="w-full flex justify-between gap-2 py-1 mb-1.5 cursor-pointer">
            <div className="min-w-0">
              <CardProviderDetails cardBrand={cardBrand} lastFourDigits={last4} textClass="!uppercase" />
              <div className="flex gap-2 ms-1 mt-1 text-xs">
                <span className="text-gray-1">Expiry date</span>
                <span>{ formattedExpMonth }/{ expYear }</span>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              {isDefault && <DefaultCardIndicator text="default" />}
              {getCardStatusIndicator(cardDetail)}
              <div className={`${expand ? "rotate-90" : ""}`}>
                <UserProfileNextIcon width={15} height={15} />
              </div>
            </div>
          </div>
          <Divider className="w-full" />
          {expand && (
            <>

              {removeWarning && (
                <div className="my-4 w-full bg-gv-yellow-light
              rounded-lg p-1 border border-taupe-1 flex gap-1 items-center">
                  <InfoIcon />
                  <div className="text-sm">You can’t delete your default payment method.</div>
                </div>
              )}
              {subscriptionWarning && (
                <div className="my-4 w-full bg-gv-yellow-light
              rounded-lg p-1 border border-taupe-1 flex gap-1 items-center">
                  <InfoIcon />
                  <div className="text-sm">This card is linked to a subscription.</div>
                </div>
              )}
              <div className="text-sm font-semibold flex flex-col items-center gap-5 pb-3 mt-5">
                {cardUpdateLoading ?
                  <CentralLoadingIndicator />:
                  !isDefault && <div className="w-full flex justify-center ps-4">
                    <div className="cursor-pointer hover:text-gv-yellow" onClick={handleSetAsDefault}>
                  Set as Default Payment
                    </div>
                  </div>}

                {loading ? <CentralLoadingIndicator /> : <div className="flex gap-2 items-center text-red-500">
                  <RemoveIcon />
                  <div className="cursor-pointer" onClick={handleRemove}>
                Remove Payment Method
                  </div>
                </div>}
              </div>
            </>
          )}
        </div>
      </div>
      {getCardStatusIndicator(cardDetail, true)}
    </>
  );
};

export default PaymentMethodCard;
